import { useMemo, useState } from 'react'
import { Card } from '@radix-ui/themes'
import TextField from '../common/TextField'
import { CheckCircle, KeyRound, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import walletService from '@/services/wallet/wallet.service'
import {
  calculatePasswordStrength,
  validatePasswordMatch
} from '@/utils/passwordStrength'

interface ChangePasswordDialogProps {
  open: boolean
  oldPassword: string
  onClose: () => void
  onSuccess: () => void
  onError: (message: string) => void
}

const ChangePasswordDialog = ({
  open,
  oldPassword,
  onClose,
  onSuccess,
  onError
}: ChangePasswordDialogProps) => {
  const { t } = useTranslation()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const passwordStrength = useMemo(
    () => calculatePasswordStrength(newPassword),
    [newPassword]
  )

  const passwordMatch = useMemo(
    () => validatePasswordMatch(newPassword, confirmPassword),
    [newPassword, confirmPassword]
  )

  const isValid =
    newPassword.length >= 8 &&
    passwordStrength.score >= 2 &&
    passwordMatch.isValid

  const getFilledBars = () => {
    if (!newPassword) return 0
    return passwordStrength.score + 1
  }

  const reset = () => {
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!isValid) return

    setIsLoading(true)
    try {
      await walletService.changePassword(oldPassword, newPassword)
      reset()
      onSuccess()
    } catch (err: any) {
      onError(err?.message || t('dialogs.verify_error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="shadow-xl bg-card w-full max-w-md justify-center relative">
          <div className="flex flex-col gap-6 p-4 place-self-center">
            <div className="flex flex-col gap-3 text-center items-center">
              <div className="bg-card-e2 rounded-full p-4 w-fit border border-border-base">
                <KeyRound className="text-primary w-6 h-6" />
              </div>
              <h2 className="text-text-main text-3xl font-bold">
                {t('settings.change_password')}
              </h2>
              <p className="text-text-secondary text-sm">
                {t('settings.change_password_dialog_desc')}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="uppercase text-xs font-semibold text-text-secondary tracking-[2.4px]">
                {t('registration.new_password')}
              </label>
              <TextField
                type="password"
                showPasswordToggle
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
              />

              <label className="uppercase text-xs font-semibold text-text-secondary tracking-[2.4px] mt-2">
                {t('registration.confirm_password')}
              </label>
              <TextField
                type="password"
                showPasswordToggle
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />

              {confirmPassword && (
                <div
                  className={`flex items-center gap-2 mt-1 text-sm ${
                    passwordMatch.isValid ? 'text-success' : 'text-red-500'
                  }`}
                >
                  {passwordMatch.isValid ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>{t(passwordMatch.message)}</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      <span>{t(passwordMatch.message)}</span>
                    </>
                  )}
                </div>
              )}

              <div className="mt-2 flex flex-col bg-card-e2 rounded-xl p-4 gap-3">
                <div className="flex justify-between">
                  <p className="font-medium text-sm text-text-secondary">
                    {t('registration.password_strength_label')}
                  </p>
                  <p className={`font-bold text-sm ${passwordStrength.color}`}>
                    {t(passwordStrength.label)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className={`w-1/4 h-2 rounded-full transition-all ${
                        index < getFilledBars()
                          ? passwordStrength.score <= 1
                            ? 'bg-red-500'
                            : passwordStrength.score === 2
                              ? 'bg-yellow-500'
                              : 'bg-success'
                          : 'bg-card'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <button
                onClick={handleSubmit}
                disabled={isLoading || !isValid}
                className="btn-gradient-fill rounded-full py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <p className="font-bold">
                  {isLoading
                    ? t('dialogs.verifying')
                    : t('settings.change_password_confirm')}
                </p>
              </button>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="text-text-secondary text-sm py-2 disabled:opacity-50"
              >
                {t('dialogs.cancel')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ChangePasswordDialog
